<script>
import { mapState, mapMutations, mapGetters } from 'vuex';
import HeartAnimation from './HeartAnimation.vue';

export default {
  name: 'TotalDonation',

  components: {
    HeartAnimation
  },

  data() {
    return {
      showHeartAnimation: false
    };
  },

  computed: {
    ...mapState('donation', {
      donation: (state) => state
    }),
    ...mapState('publicOrgSettings', ['orgDetails']),
    ...mapGetters('donation', ['totalDonation', 'chargeFees', 'totalFees', 'hasEndDate']),
    ...mapGetters('campaign', ['matchingSettings']),
    ...mapState('campaign', ['id', 'settings']),

    coveringFees: {
      get() {
        return this.donation.payCoverFees;
      },
      set(value) {
        this.SET_PAY_COVER_FEES(value);
        if (value === true) {
          this.triggerHeartAnimation();
        }
      }
    },
    frequency() {
      return this.donation.frequency;
    },
    isRecurring() {
      return this.frequency && this.frequency !== 'single';
    },
    showRecurringFees() {
      if (this.isRecurring) {
        return true;
      }
      if (this.hasEndDate) {
        return this.donation.numCharges;
      }
      return false;
    },
    paymentSummaryFormatted() {
      const payments = {
        daily: this.$t('payment.dayPayment'),
        weekly: this.$t('payment.weekPayment'),
        monthly: this.$t('payment.monthPayment'),
        yearly: this.$t('payment.yearPayment')
      };
      return payments[this.frequency];
    },
    totalDonationText() {
      const ctaTerm = this.settings?.ctaTerm?.toLowerCase();
      if (!ctaTerm) {
        return this.$t('payment.total.donate');
      }
      return this.$t(`payment.total.${ctaTerm}`);
    },
    matchingRatio() {
      const ratio = Number(this.matchingSettings?.matchingRatio);
      return Number.isFinite(ratio) && ratio > 0 ? ratio : 0;
    },
    showMatchingTotal() {
      return Boolean(this.matchingSettings?.matchingEnabled && this.matchingRatio);
    },
    matchedTotalDonation() {
      return Number((this.totalDonation * (1 + this.matchingRatio)).toFixed(2));
    }
  },
  methods: {
    ...mapMutations('donation', ['SET_PAY_COVER_FEES']),

    triggerHeartAnimation() {
      this.showHeartAnimation = false;

      this.$nextTick(() => {
        this.showHeartAnimation = true;
      });
    },
    handleCoverFeesToggle() {
      if (this.settings.hideCoverFees) {
        return;
      }
      this.coveringFees = !this.coveringFees;
    }
  }
};
</script>

<template>
  <div class="total-donation my-5">
    <!-- <hr class="my-2 border-none bg-gray-disabled" /> -->
    <template v-if="!hasEndDate">
      <div class="flex items-center justify-between">
        <span class="body-text-extra-bold">{{ totalDonationText }}:</span>
        <app-price :amount="totalDonation" :currency="donation.currency" :frequency="donation.frequency" :show-frequency="isRecurring" />
      </div>
      <div v-if="showMatchingTotal" class="flex items-center justify-between mt-2">
        <span class="body-text-extra-bold">{{ $t('payment.matching.after') }}:</span>
        <app-price :amount="matchedTotalDonation" :currency="donation.currency" :frequency="donation.frequency" :show-frequency="isRecurring" />
      </div>
    </template>

    <div v-else-if="isRecurring && hasEndDate">
      <div class="flex justify-between">
        <span class="body-text-extra-bold">{{ paymentSummaryFormatted }}:</span>
        <app-price
          :amount="donation.amount + (donation.payCoverFees ? chargeFees : 0)"
          :currency="donation.currency"
          :frequency="donation.frequency"
          show-frequency
        />
      </div>
      <div class="flex justify-between subtext-small-text-semibold">
        <span>{{ totalDonationText }}:</span>
        <app-price :amount="totalDonation" :currency="donation.currency" :frequency="donation.frequency" />
      </div>
      <div v-if="showMatchingTotal" class="flex justify-between subtext-small-text-semibold">
        <span>{{ $t('payment.matching.after') }}:</span>
        <app-price :amount="matchedTotalDonation" :currency="donation.currency" :frequency="donation.frequency" />
      </div>
    </div>
    <div v-if="!settings.hideCoverFees || (settings.hideCoverFees && settings.coverFeesChecked)" class="body-text-regular mt-1">
      <div class="cover-fees-container rounded-lg p-3 mt-2.5 relative" :class="{ 'covering-fees bg-primary-opacity': coveringFees }">
        <!-- Heart Animation Component -->
        <heart-animation v-if="showHeartAnimation" :top="10" :left="12" @animation-complete="showHeartAnimation = false" />
        <div v-if="settings.hideCoverFees" class="flex items-center select-none">
          <app-icon name="heart" type="svg" width="24" height="24" class="mr-1" />
          <span class="text-lg text-black-00 font-bold">{{ orgDetails.name }} takes 0% from your gift! </span>
        </div>
        <app-checkbox v-else v-model="coveringFees" variant="emphasized">
          {{ $t('payment.coverFees') }}
        </app-checkbox>
        <p class="cover-fees-description text-black-01 font-normal mt-1.5 cursor-pointer" @click="handleCoverFeesToggle">
          <template v-if="!settings.hideCoverFees">
            Many donors choose to cover the fees — join them by adding
            <app-price :amount="hasEndDate ? chargeFees : totalFees" :currency="donation.currency" />
          </template>
          <template v-else>
            The processing fee of <app-price :amount="hasEndDate ? chargeFees : totalFees" :currency="donation.currency" /> is pre-set to ensure your whole gift
            goes to the beneficiaries.
          </template>
        </p>
      </div>
    </div>
  </div>
</template>
